import React, { useState, useEffect, useRef } from 'react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import { motion, useScroll, useTransform, useMotionValue } from 'framer-motion';
import { 
  Wind, Box, Square, CornerDownRight, CircleDashed, CheckCircle2, 
  Factory, Settings, Clock, Truck, Wrench, MapPin, Gauge, Hammer, 
  Lightbulb, AlertCircle, Info 
} from 'lucide-react';

// --- Assets & Icons ---
const DuctIcons = {
  Spiral: ({ className }) => <CircleDashed className={className} />,
  Rectangular: ({ className }) => <Box className={className} />,
  Flat: ({ className }) => <Square className={className} />,
  LShaped: ({ className }) => <CornerDownRight className={className} />,
  Collar: ({ className }) => <Settings className={className} />,
};

// --- Helper Components ---

// 1. Typewriter Effect
const TypewriterText = ({ text, className, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimeout = setTimeout(() => {
      setStarted(true);
    }, delay * 1000);
    return () => clearTimeout(startTimeout);
  }, [delay]);

  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.substring(0, i + 1));
      i++;
      if (i > text.length) clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, [text, started]);

  return (
    <span className={className}>
      {displayedText}
      <motion.span
        animate={{ opacity: [0, 1, 0] }}
        transition={{ repeat: Infinity, duration: 0.8 }}
        className="cursor-blink"
      />
    </span>
  );
};

// 2. Custom Cursor Glow
const CursorGlow = () => {
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  useEffect(() => {
    const moveCursor = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener('mousemove', moveCursor);
    return () => window.removeEventListener('mousemove', moveCursor);
  }, [mouseX, mouseY]);

  return (
    <motion.div
      className="cursor-glow"
      style={{
        x: mouseX,
        y: mouseY,
        translateX: '-50%',
        translateY: '-50%',
      }}
    />
  );
};

// 3. Tilt Card (Standard CSS Classes)
const TiltCard = ({ title, Icon, index }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-0.5, 0.5], [15, -15]);
  const rotateY = useTransform(x, [-0.5, 0.5], [-15, 15]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      className="duct-card"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="card-bg-gradient" />
      
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="card-icon-wrapper"
      >
        <Icon className="card-icon" />
      </motion.div>

      <div className="card-title" style={{ transform: "translateZ(20px)" }}>
        {title}
      </div>

      <div className="card-shine" />
      <div className="card-bottom-bar" />
    </motion.div>
  );
};

// 4. Swinging Gallery (Standard CSS Classes)
const SwingingGallery = () => {
    const images = [
        { 
            src: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600", 
            alt: "Blue Exposed Ducts in Modern Office",
            label: "Corporate Installations" 
        },
        { 
            src: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&q=80&w=600", 
            alt: "Colorful Cafe Ductwork",
            label: "Restaurant & Cafe" 
        },
        { 
            src: "https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?auto=format&fit=crop&q=80&w=600", 
            alt: "HVAC System Chart",
            label: "Main Air Duct Systems" 
        },
        { 
            src: "https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=600", 
            alt: "Lime Green Office Ducts",
            label: "Custom Color Finishes" 
        }
    ];

    return (
        <div className="gallery-section">
             <div className="section-header">
                <h2 className="heading-primary">Our Work Gallery</h2>
                <div className="heading-underline" />
            </div>

            <div className="wire-line" />

            <div className="gallery-grid">
                {images.map((img, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ rotate: 0 }}
                        animate={{ rotate: idx % 2 === 0 ? [1.5, -1.5, 1.5] : [-1.5, 1.5, -1.5] }}
                        transition={{ 
                            duration: 4 + idx, 
                            repeat: Infinity, 
                            ease: "easeInOut" 
                        }}
                        style={{ transformOrigin: "top center" }}
                        className="swinging-frame-wrapper"
                    >
                        <div className="pin" />
                        
                        <div className="polaroid-frame">
                            <div className="polaroid-image-container">
                                <img src={img.src} alt={img.alt} className="polaroid-image" />
                                <div className="polaroid-overlay" />
                            </div>
                            <p className="polaroid-label">{img.label}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// 5. Fact Card (New Component)
const FactCard = ({ icon: Icon, title, text }) => (
  <motion.div 
    className="fact-card"
    whileHover={{ scale: 1.05 }}
    initial={{ opacity: 0, x: 20 }}
    whileInView={{ opacity: 1, x: 0 }}
  >
    <div className="fact-icon-bg">
      <Icon className="fact-icon" />
    </div>
    <div className="fact-content">
      <h4 className="fact-title">{title}</h4>
      <p className="fact-text">{text}</p>
    </div>
    <div className="blinking-dot" />
  </motion.div>
);

// --- Main Component ---

export default function DuctManufacturing() {
  const scrollRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: scrollRef });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <div ref={scrollRef} className="app-container">
        <Header></Header>
      <CursorGlow />

      <main className="main-content">
        
        {/* --- SECTION 1: HERO --- */}
        
        <section className="hero-section">
          <div className="hero-text-col">
            <div className="title-wrapper">
                <h1 className="hero-title">
                    <TypewriterText text="In-House Duct Manufacturing Unit" delay={0.2} className="typewriter-text" />
                </h1>
            </div>
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="hero-description"
            >
                <p>
                  Sky Breeze operates a dedicated duct fabrication facility, equipped to deliver custom ducting solutions for commercial AC projects. With a 6-member manufacturing team, we ensure accuracy, thorough quality checks, and on-time delivery.
                </p>
                
                {/* Precision Block */}
                <div className="precision-block">
                    <Gauge className="precision-icon" />
                    <div>
                        <h4 className="precision-title">Precision Engineering</h4>
                        <p className="precision-desc">
                            Advanced calibration for exact pressure & flow specifications.
                        </p>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className="offerings-box"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
            >
                <h3 className="offerings-title">
                    <Factory className="icon-sm" /> What We Offer:
                </h3>
                <ul className="offerings-list">
                    {[
                        "Customized duct design based on site dimensions",
                        "Spiral, rectangular, flat, L-shaped ducts",
                        "High-quality galvanized & aluminum raw materials",
                        "Skilled workforce with timely production",
                        "Quality assurance on every unit manufactured"
                    ].map((item, i) => (
                        <li key={i} className="offering-item">
                            <CheckCircle2 className="check-icon" />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            </motion.div>

            {/* Airflow Bar */}
            <div className="airflow-container">
              <div className="airflow-labels">
                <span className="airflow-label">Airflow Efficiency</span>
                <span className="airflow-value">99.8%</span>
              </div>
              <div className="airflow-track">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "99.8%" }}
                  transition={{ duration: 2, ease: "easeOut" }}
                  className="airflow-fill"
                >
                  <motion.div 
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    className="airflow-shimmer"
                  />
                </motion.div>
              </div>
            </div>
          </div>

          {/* Hero Image */}
          <motion.div style={{ y: yParallax }} className="hero-image-col">
            <motion.div 
                className="image-frame"
                initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 1 }}
                whileHover={{ scale: 1.02, rotate: 1 }}
            >
                <div className="image-wrapper">
                    <img 
                        src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=1200" 
                        alt="Ultra-clean industrial duct fabrication unit"
                        className="hero-img"
                    />
                    <div className="image-overlay" />
                    
                    <div className="image-badge">
                        <div className="badge-shine" />
                        <p className="badge-label">Location</p>
                        <p className="badge-text">Coimbatore Fabrication Unit</p>
                    </div>
                </div>
                <div className="glow-border" />
            </motion.div>
            
            {/* Background Blobs */}
            <div className="blob blob-blue" />
            <div className="blob blob-grey" />
          </motion.div>
        </section>

        {/* --- SECTION 2: PRODUCTS & FACTS --- */}
        <section className="products-section">
          <div className="section-header center-text">
            <div className="heading-wrapper">
                <h2 className="heading-primary">
                   <TypewriterText text="Duct Factory Unit – Precision. Performance. Perfection." delay={0} className="typewriter-text-lg" />
                </h2>
                <div className="heading-underline-skew" />
            </div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="subheading"
            >
              Located in Coimbatore, our fabrication unit produces high-grade ducting for optimal airflow management.
            </motion.p>
          </div>
          
          {/* Engineering Facts */}
          <div className="facts-grid">
             <FactCard 
               icon={Info}
               title="Did You Know?"
               text="Circular ducts are naturally stronger and can handle higher pressure with less material than rectangular ones."
             />
             <FactCard 
               icon={Lightbulb}
               title="Energy Efficiency"
               text="Our smooth inner-surface ducts reduce friction loss by 15%, lowering overall AC energy consumption."
             />
             <FactCard 
               icon={AlertCircle}
               title="Zinc Coating"
               text="We use G90 grade galvanized steel, ensuring rust protection for 20+ years even in humid climates."
             />
          </div>

          {/* 3D Cards Grid */}
          <div className="cards-grid">
            {[
              { title: "Spiral Ducts", icon: DuctIcons.Spiral },
              { title: "Rectangular Ducts", icon: DuctIcons.Rectangular },
              { title: "Flat Ducts", icon: DuctIcons.Flat },
              { title: "L-Shaped Ducts", icon: DuctIcons.LShaped },
              { title: "Collar Fittings", icon: DuctIcons.Collar },
            ].map((card, idx) => (
              <TiltCard 
                key={idx} 
                index={idx} 
                title={card.title} 
                Icon={card.icon} 
              />
            ))}
            
            {/* QA Card */}
            <motion.div 
                className="qa-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 }}
            >
                <div className="qa-bg-pattern" />
                <div className="qa-shine" />
                
                <CheckCircle2 className="qa-icon" />
                <h3 className="qa-title">Quality Assured</h3>
                <p className="qa-text">
                   Strict quality checks ensure airflow efficiency, durability, and flawless installation.
                </p>
                <Hammer className="qa-decor-icon" />
            </motion.div>
          </div>
        </section>

        {/* --- SECTION 3: SWINGING GALLERY --- */}
        <SwingingGallery />

        {/* --- SECTION 4: FEATURES (Darker Glow & Animated Icons) --- */}
        <section className="features-section">
            <motion.div 
                className="features-container"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
            >
                <div className="features-bg-blob" />
                <div className="features-bottom-border" />

                <div className="section-header center-text relative-z">
                    <h2 className="heading-primary">
                        Why Choose Our Duct Unit?
                    </h2>
                    <div className="heading-underline" />
                </div>

                <div className="features-grid relative-z">
                    {[
                        { 
                            icon: Clock, 
                            text: "Quick turn around on large orders", 
                            color: "#ea580c", // Dark Orange
                            bg: "rgba(255, 237, 213, 0.5)", 
                            shadow: "#9a3412" // Deep Burnt Orange
                        },
                        { 
                            icon: Truck, 
                            text: "On-site delivery coordination", 
                            color: "#16a34a", // Dark Green
                            bg: "rgba(220, 252, 231, 0.5)",
                            shadow: "#14532d" // Deep Forest Green
                        },
                        { 
                            icon: Wrench, 
                            text: "Custom fittings on request", 
                            color: "#7c3aed", // Dark Violet
                            bg: "rgba(237, 233, 254, 0.5)",
                            shadow: "#4c1d95" // Deep Royal Violet
                        },
                        { 
                            icon: MapPin, 
                            text: "Trusted by contractors across Tamil Nadu", 
                            color: "#db2777", // Dark Pink
                            bg: "rgba(252, 231, 243, 0.5)",
                            shadow: "#831843" // Deep Raspberry
                        }
                    ].map((item, idx) => (
                        <motion.div 
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.15 }}
                            whileHover={{ y: -8, scale: 1.03 }}
                            className="feature-item"
                            style={{ 
                                '--feature-color': item.color,
                                '--feature-bg': item.bg,
                                '--feature-shadow': item.shadow
                            }}
                        >
                            <div className="feature-icon-box">
                                <item.icon className="feature-icon" />
                            </div>
                            <p className="feature-text">
                                {item.text}
                            </p>
                        </motion.div>
                    ))}
                </div>
                
            </motion.div>
        </section>
                   
      </main>

      {/* --- DUCT.CSS CONTENT --- */}
      <style>{`
        /* --- RESET & VARIABLES --- */
        :root {
            --primary-blue:  #1a3a5c;
            --dark-blue: #1e3a8a;
            --light-blue: #dbeafe;
            --hover-blue:  #1e3a8a;
            --slate-50: #f8fafc;
            --slate-200: #e2e8f0;
            --slate-600: #475569;
            --slate-800: #1e293b;
            --slate-900: #0f172a;
            --white: #ffffff;
            --font-main: 'ui-sans-serif', system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            --font-mono: 'Courier Prime', monospace;
            --font-hand: 'Caveat', cursive;
        }

        * { box-sizing: border-box; }

        body {
            margin: 0;
            font-family: var(--font-main);
            background-color: var(--slate-50);
            color: var(--slate-800);
            overflow-x: hidden;
        }

        @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap');

        /* --- ANIMATIONS --- */
        @keyframes shine {
            0% { transform: translateX(-150%) skewX(-12deg); }
            50% { transform: translateX(150%) skewX(-12deg); }
            100% { transform: translateX(150%) skewX(-12deg); }
        }
        
        @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
        }
        
        /* Darker, More Intense Pulse */
        @keyframes pulse-glow {
            0% { 
                box-shadow: 0 0 0 0 rgba(0,0,0,0); 
                border-color: transparent;
            }
            50% { 
                box-shadow: 0 0 12px 3px var(--feature-shadow); 
                border-color: var(--feature-color);
            }
            100% { 
                box-shadow: 0 0 0 0 rgba(0,0,0,0); 
                border-color: transparent;
            }
        }
        
        /* Icon Breathing & Scale Effect */
        @keyframes icon-pulse {
             0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
             50% { transform: scale(1.15); filter: drop-shadow(0 0 6px var(--feature-color)); }
        }

        /* --- LAYOUT UTILS --- */
        .app-container {
            position: relative;
            min-height: 100vh;
        }

        .main-content {
            position: relative;
            z-index: 10;
            padding-top: 3rem;
            padding-bottom: 5rem;
            padding-left: 1.5rem;
            padding-right: 1.5rem;
            max-width: 80rem;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 4rem;
        }

        /* --- HERO SECTION --- */
        .hero-section {
            display: grid;
            gap: 3rem;
            align-items: center;
            min-height: 60vh;
        }

        @media (min-width: 1024px) {
            .hero-section { grid-template-columns: 1fr 1fr; }
            .hero-text-col { order: 1; }
            .hero-image-col { order: 2; }
        }

        .hero-title {
            font-size: 2.25rem;
            font-weight: 700;
            line-height: 1.2;
            color: var(--primary-blue);
            min-height: 3em;
            padding:20px;
        }

        @media (min-width: 768px) {
            .hero-title { font-size: 3rem; min-height: auto; }
        }

        .typewriter-text {
            font-family: var(--font-mono);
            color: var(--primary-blue);
            transition: color 0.3s;
        }
        
        .typewriter-text:hover {
            color: var(--dark-blue);
            text-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
        }
        
        .cursor-blink {
            display: inline-block;
            width: 0.25rem;
            height: 1em;
            background-color: var(--primary-blue);
            margin-left: 0.25rem;
            vertical-align: middle;
        }

        .hero-description {
            font-size: 1.125rem;
            color: var(--slate-600);
            line-height: 1.6;
            max-width: 36rem;
            margin-bottom: 2rem;
        }

        .precision-block {
            display: flex;
            gap: 1rem;
            padding: 1rem;
            background-color: rgba(219, 234, 254, 0.5);
            border-radius: 0.75rem;
            border: 1px solid rgba(219, 234, 254, 0.8);
            margin-top: 1rem;
        }

        .precision-icon {
            width: 1.5rem;
            height: 1.5rem;
            color: var(--primary-blue);
            flex-shrink: 0;
        }
        
        .precision-title {
            margin: 0;
            font-weight: 700;
            font-size: 0.875rem;
            color: var(--slate-800);
        }
        
        .precision-desc {
            margin: 0.25rem 0 0 0;
            font-size: 0.75rem;
            color: var(--slate-600);
        }

        .offerings-box {
            background-color: rgba(255, 255, 255, 0.6);
            padding: 1.5rem;
            border-radius: 1rem;
            border: 1px solid var(--slate-200);
            backdrop-filter: blur(4px);
            margin-bottom: 2rem;
            transition: box-shadow 0.3s;
        }
        
        .offerings-box:hover {
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .offerings-title {
            font-weight: 700;
            color: var(--dark-blue);
            margin-top: 0;
            margin-bottom: 0.5rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }

        .offerings-list {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            font-size: 0.875rem;
            color: var(--slate-600);
        }

        .offering-item {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
        }

        .check-icon {
            width: 1rem;
            height: 1rem;
            color: var(--primary-blue);
            margin-top: 0.125rem;
            flex-shrink: 0;
        }

        /* Airflow Bar */
        .airflow-container {
            width: 100%;
            max-width: 28rem;
        }

        .airflow-labels {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
        }

        .airflow-label {
            font-size: 0.75rem;
            font-weight: 600;
            color: var(--slate-600);
            text-transform: uppercase;
            letter-spacing: 0.1em;
        }

        .airflow-value {
            font-size: 0.75rem;
            font-weight: 700;
            color: var(--primary-blue);
        }

        .airflow-track {
            height: 0.75rem;
            width: 100%;
            background-color: var(--slate-200);
            border-radius: 9999px;
            overflow: hidden;
            position: relative;
            box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
        }

        .airflow-fill {
            height: 100%;
            background: linear-gradient(90deg, #60a5fa, #1d4ed8);
            border-radius: 9999px;
            position: relative;
        }

        .airflow-shimmer {
            position: absolute;
            top: 0;
            bottom: 0;
            width: 50%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            transform: skewX(-12deg);
        }

        /* Hero Image */
        .hero-image-col {
            position: relative;
        }

        .image-frame {
            position: relative;
            border-radius: 1.5rem;
            overflow: hidden;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            border: 6px solid white;
            z-index: 20;
        }

        .image-wrapper {
            aspect-ratio: 4/3;
            background-color: var(--slate-200);
            position: relative;
            overflow: hidden;
        }

        .hero-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.7s;
        }
        
        .image-frame:hover .hero-img {
            transform: scale(1.1);
        }

        .image-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, rgba(30, 58, 138, 0.4), transparent);
        }

        .image-badge {
            position: absolute;
            bottom: 1.5rem;
            left: 1.5rem;
            right: 1.5rem;
            background-color: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(4px);
            padding: 0.75rem 1.25rem;
            border-radius: 0.75rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }

        .badge-shine {
            position: absolute;
            inset: 0;
            background: linear-gradient(90deg, transparent, rgba(191, 219, 254, 0.5), transparent);
            transform: translateX(-100%);
            animation: shine 3s infinite;
        }

        .badge-label {
            font-size: 0.75rem;
            color: var(--slate-600);
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin: 0 0 0.25rem 0;
        }

        .badge-text {
            font-weight: 700;
            color: var(--slate-900);
            margin: 0;
        }

        .glow-border {
            position: absolute;
            inset: 0;
            border-radius: 1rem;
            pointer-events: none;
            box-shadow: 0 0 0 1px rgba(255,255,255,0.2);
            transition: box-shadow 0.5s;
        }
        
        .image-frame:hover .glow-border {
            box-shadow: 0 0 60px rgba(37, 99, 235, 0.4);
        }

        .blob {
            position: absolute;
            border-radius: 50%;
            filter: blur(40px);
            z-index: -10;
        }
        
        .blob-blue {
            top: -3rem;
            right: -3rem;
            width: 16rem;
            height: 16rem;
            background-color: var(--light-blue);
            opacity: 0.6;
            animation: blink 4s infinite alternate;
        }
        
        .blob-grey {
            bottom: -3rem;
            left: -3rem;
            width: 12rem;
            height: 12rem;
            background-color: var(--slate-200);
            opacity: 0.6;
        }

        /* --- PRODUCTS & FACTS --- */
        .section-header {
            text-align: center;
            margin-bottom: 2rem;
        }

        .heading-wrapper {
            display: inline-block;
            position: relative;
        }

        .heading-primary {
            font-size: 2rem;
            font-weight: 700;
            color: var(--dark-blue);
            margin: 0;
            position: relative;
            z-index: 10;
            cursor: default;
            transition: color 0.3s;
        }
        
        .heading-primary:hover {
            color: var(--hover-blue);
            text-shadow: 0 0 15px rgba(59, 130, 246, 0.4);
        }

        @media (min-width: 768px) {
            .heading-primary { font-size: 2.25rem; }
        }

        .heading-underline-skew {
            position: absolute;
            bottom: -0.5rem;
            left: 0;
            width: 100%;
            height: 0.75rem;
            background-color: rgba(219, 234, 254, 0.5);
            transform: skewX(-12deg);
            z-index: 0;
        }

        .subheading {
            color: var(--slate-600);
            max-width: 42rem;
            margin: 1rem auto 0;
            font-weight: 500;
        }

        /* Engineering Facts Grid */
        .facts-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            margin-bottom: 3rem;
        }
        
        @media (min-width: 768px) {
            .facts-grid { grid-template-columns: repeat(3, 1fr); }
        }

        .fact-card {
            background: white;
            border: 1px solid var(--slate-200);
            border-radius: 1rem;
            padding: 1.5rem;
            position: relative;
            overflow: hidden;
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
            transition: transform 0.3s, border-color 0.3s;
        }
        
        .fact-card:hover {
            border-color: var(--primary-blue);
        }
        
        .fact-icon-bg {
            background: var(--light-blue);
            width: 2.5rem;
            height: 2.5rem;
            border-radius: 0.5rem;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 1rem;
        }
        
        .fact-icon {
            color: var(--primary-blue);
            width: 1.25rem;
            height: 1.25rem;
        }
        
        .fact-title {
            margin: 0 0 0.5rem 0;
            color: var(--slate-900);
            font-size: 1rem;
        }
        
        .fact-text {
            margin: 0;
            color: var(--slate-600);
            font-size: 0.85rem;
            line-height: 1.5;
        }
        
        .blinking-dot {
            position: absolute;
            top: 1rem;
            right: 1rem;
            width: 6px;
            height: 6px;
            background: var(--primary-blue);
            border-radius: 50%;
            opacity: 0;
            transition: opacity 0.3s;
        }
        
        .fact-card:hover .blinking-dot {
            opacity: 1;
            animation: blink 1s infinite;
        }

        /* Duct Cards Grid */
        .cards-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 1.5rem;
            padding: 0 1rem;
        }

        @media (min-width: 768px) {
            .cards-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (min-width: 1024px) {
            .cards-grid { grid-template-columns: repeat(3, 1fr); }
        }

        /* Tilt Card Styles */
        .duct-card {
            position: relative;
            width: 100%;
            height: 13rem;
            background-color: white;
            border-radius: 1rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
            border: 1px solid var(--slate-200);
            padding: 1.5rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            cursor: pointer;
            overflow: hidden;
            transition: border-color 0.3s;
        }
        
        .duct-card:hover {
            border-color: #93c5fd;
        }

        .card-bg-gradient {
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom right, #eff6ff, white);
            opacity: 0.5;
            transition: opacity 0.5s;
        }
        
        .duct-card:hover .card-bg-gradient {
            opacity: 1;
        }

        .card-icon-wrapper {
            position: relative;
            z-index: 10;
            color: #94a3b8;
            transition: color 0.3s;
        }
        
        .duct-card:hover .card-icon-wrapper {
            color: var(--primary-blue);
        }

        .card-icon {
            width: 3rem;
            height: 3rem;
            filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1));
        }

        @media (min-width: 768px) {
            .card-icon { width: 4rem; height: 4rem; }
        }

        .card-title {
            position: relative;
            z-index: 10;
            font-weight: 700;
            color: var(--slate-600);
            font-size: 1.125rem;
            transition: all 0.3s;
        }
        
        .duct-card:hover .card-title {
            color: var(--dark-blue);
            transform: scale(1.05);
        }

        .card-shine {
            position: absolute;
            inset: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent);
            transform: translateX(-100%) skewX(12deg);
            z-index: 20;
            pointer-events: none;
        }
        
        .duct-card:hover .card-shine {
            animation: shine 1.5s ease-in-out;
        }

        .card-bottom-bar {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, transparent, var(--primary-blue), transparent);
            transform: scaleX(0);
            transition: transform 0.5s;
        }
        
        .duct-card:hover .card-bottom-bar {
            transform: scaleX(1);
        }

        /* QA Card Specifics */
        .qa-card {
            background: linear-gradient(to bottom right, #2563eb, #1d4ed8);
            border-radius: 1rem;
            padding: 1.5rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
            color: white;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
            position: relative;
            overflow: hidden;
            transition: transform 0.3s, box-shadow 0.3s;
        }
        
        .qa-card:hover {
            transform: translateY(-0.5rem);
            box-shadow: 0 25px 50px -12px rgba(37, 99, 235, 0.3);
        }

        .qa-bg-pattern {
            position: absolute;
            inset: 0;
            background-image: url('https://www.transparenttextures.com/patterns/carbon-fibre.png');
            opacity: 0.1;
        }

        .qa-shine {
            position: absolute;
            inset: 0;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
            transform: translateX(-100%);
        }
        
        .qa-card:hover .qa-shine {
            animation: shine 1s ease-out;
        }

        .qa-icon {
            width: 3rem;
            height: 3rem;
            margin-bottom: 1rem;
            color: #bfdbfe;
            transition: transform 0.3s;
        }
        
        .qa-card:hover .qa-icon {
            transform: scale(1.1);
        }

        .qa-title {
            font-weight: 700;
            font-size: 1.25rem;
            margin: 0 0 0.5rem 0;
            position: relative;
            z-index: 10;
        }

        .qa-text {
            color: #dbeafe;
            font-size: 0.875rem;
            line-height: 1.6;
            margin: 0;
            position: relative;
            z-index: 10;
        }

        .qa-decor-icon {
            position: absolute;
            bottom: -1rem;
            right: -1rem;
            width: 6rem;
            height: 6rem;
            color: rgba(30, 58, 138, 0.2);
            transform: rotate(45deg);
        }

        /* --- GALLERY SECTION --- */
        .gallery-section {
            padding: 2rem 0 1rem 0;
            position: relative;
        }

        .heading-underline {
            height: 0.25rem;
            width: 5rem;
            background-color: var(--primary-blue);
            margin: 0 auto;
            border-radius: 9999px;
        }

        .wire-line {
            position: absolute;
            top: 6rem;
            left: 0;
            width: 100%;
            height: 2px;
            background-color: #cbd5e1;
            z-index: 0;
            display: none;
        }

        @media (min-width: 768px) {
            .wire-line { display: block; }
        }

        .gallery-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 2rem;
            padding: 0 1rem;
        }

        @media (min-width: 768px) {
            .gallery-grid { gap: 1rem; }
        }

        .swinging-frame-wrapper {
            position: relative;
            z-index: 10;
        }

        .pin {
            position: absolute;
            top: -0.75rem;
            left: 50%;
            transform: translateX(-50%);
            width: 1rem;
            height: 1rem;
            border-radius: 50%;
            background: radial-gradient(circle at 30% 30%, #ef4444, #b91c1c);
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            z-index: 20;
            border: 2px solid white;
        }

        .polaroid-frame {
            background: white;
            padding: 0.75rem;
            padding-bottom: 2rem;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
            width: 16rem;
            border: 1px solid var(--slate-200);
            transition: box-shadow 0.3s;
        }
        
        .polaroid-frame:hover {
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2);
        }

        .polaroid-image-container {
            aspect-ratio: 4/3;
            background-color: var(--slate-200);
            overflow: hidden;
            margin-bottom: 0.75rem;
            position: relative;
        }

        .polaroid-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.7s;
        }
        
        .polaroid-image-container:hover .polaroid-image {
            transform: scale(1.1);
        }

        .polaroid-overlay {
            position: absolute;
            inset: 0;
            background-color: rgba(30, 58, 138, 0.1);
            transition: background-color 0.3s;
        }
        
        .polaroid-image-container:hover .polaroid-overlay {
            background-color: transparent;
        }

        .polaroid-label {
            text-align: center;
            font-family: var(--font-hand);
            font-size: 1.25rem;
            color: var(--slate-600);
            font-weight: 600;
            transform: rotate(-1deg);
            margin: 0;
        }

        /* --- FEATURES SECTION --- */
        .features-section {
            position: relative;
            padding-bottom: 2.5rem;
        }

        .features-container {
            background-color: rgba(255, 255, 255, 0.8);
            backdrop-filter: blur(12px);
            border-radius: 1.5rem;
            padding: 2rem;
            border: 1px solid #dbeafe;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
            position: relative;
            overflow: hidden;
        }

        @media (min-width: 768px) {
            .features-container { padding: 3rem; }
        }

        .features-bg-blob {
            position: absolute;
            top: -8rem;
            right: -8rem;
            width: 24rem;
            height: 24rem;
            background-color: var(--light-blue);
            border-radius: 50%;
            filter: blur(64px);
            opacity: 0.8;
        }

        .features-bottom-border {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 4px;
            background: linear-gradient(90deg, #60a5fa, #22d3ee, #60a5fa);
        }

        .relative-z {
            position: relative;
            z-index: 10;
        }

        .features-grid {
            display: grid;
            gap: 2rem;
            margin-top: 3rem;
        }

        @media (min-width: 768px) {
            .features-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (min-width: 1024px) {
            .features-grid { grid-template-columns: repeat(4, 1fr); }
        }

        .feature-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 1rem;
            background: white;
            padding: 1.5rem;
            border-radius: 1rem;
            border: 1px solid var(--slate-50);
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
            transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        
        .feature-item:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 35px -5px var(--feature-shadow, rgba(0,0,0,0.1));
            border-color: var(--feature-color, #e2e8f0);
        }

        .feature-icon-box {
            width: 4rem;
            height: 4rem;
            background-color: var(--feature-bg, #f8fafc);
            border: 1px solid #e2e8f0;
            border-radius: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--feature-color, var(--primary-blue));
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            position: relative;
            overflow: hidden;
            transition: all 0.3s;
            animation: pulse-glow 3s infinite;
        }
        
        .feature-item:hover .feature-icon-box {
            background: white;
            border-color: var(--feature-color);
            transform: scale(1.1);
        }

        .feature-icon {
            width: 2rem;
            height: 2rem;
            stroke-width: 1.5;
            position: relative;
            z-index: 10;
            animation: icon-pulse 3s infinite; /* Icon Effects */
        }

        .feature-text {
            font-weight: 500;
            color: var(--slate-600);
            max-width: 200px;
            margin: 0;
            line-height: 1.4;
            transition: color 0.3s;
        }
        
        .feature-item:hover .feature-text {
            color: var(--slate-900);
            font-weight: 600;
        }

        /* Helper for Cursor */
        .cursor-glow {
            position: fixed;
            top: 0;
            left: 0;
            width: 2rem;
            height: 2rem;
            pointer-events: none;
            z-index: 9999;
            border-radius: 50%;
            background-color: #60a5fa;
            mix-blend-mode: multiply;
            filter: blur(24px);
            opacity: 0.4;
        }
      `}</style>
       <Footer></Footer>
    </div>
  );
}